import React from 'react';
import { BRAND, SUPPORT_EMAIL, HAS_TELEGRAM } from '../../../config/region';

/*
 * "cooperation" in every language it has been translated into.
 *
 * The Russian original points authors at a Russian-language Telegram account. That contact does
 * not exist for Plotex, so the translations route authors to the support mailbox instead of
 * translating a channel a European author cannot use. They no longer offer the /authors page
 * either: it is routed in russia only (see App.js), and these translations only render on europe.
 */
const post = {
  slug: 'cooperation',
  datePublished: '2025-02-01',
  dateModified: '2025-06-01',

  russian: {
    title: '🤝 Сотрудничество',
    description: `Стань автором ${BRAND} — создавай новеллы и зарабатывай.`,
    seoTitle: `Сотрудничество — ${BRAND}`,
    seoDescription: `Станьте автором ${BRAND}: создавайте интерактивные новеллы и получайте 20% от дохода. Свяжитесь с нами для сотрудничества.`,
    content: (
      <>
        <h2>Стань автором {BRAND}</h2>
        <p>
          Мы в активном поиске талантливых авторов, которые хотят создавать
          уникальные интерактивные истории и делиться ими с нашим сообществом.
        </p>
        <p>
          За свою новеллу автор получает 20% от заработанного с новеллы. Это твой
          шанс превратить творчество в источник дохода — создавай миры, которые
          будут вдохновлять тысячи игроков.
        </p>
        <h2>Контакты</h2>
        {/* Telegram is a genuine external contact and only exists for {BRAND}; HAS_TELEGRAM is
            false in europe, so plotex.io/ru shows the support mailbox alone. */}
        {HAS_TELEGRAM && (
          <p>
            Telegram:{' '}
            <a href="https://t.me/novelladmin" target="_blank" rel="noopener noreferrer">
              @novelladmin
            </a>
          </p>
        )}
        <p>
          Email:{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  english: {
    title: '🤝 Work with us',
    description: `Become a ${BRAND} author — build novels and earn from them.`,
    seoTitle: `Work with us — ${BRAND}`,
    seoDescription: `Become a ${BRAND} author: build interactive novels and receive 20% of the revenue they earn. Get in touch to start working with us.`,
    content: (
      <>
        <h2>Become a {BRAND} author</h2>
        <p>
          We are actively looking for talented authors who want to build unique interactive
          stories and share them with our community.
        </p>
        <p>
          Authors receive 20% of what their novel earns. This is your chance to turn writing
          into an income — build worlds that will inspire thousands of players.
        </p>
        <h2>Get in touch</h2>
        <p>
          Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  german: {
    title: '🤝 Zusammenarbeit',
    description: `Werde ${BRAND}-Autor — erstelle Novellen und verdiene damit.`,
    seoTitle: `Zusammenarbeit — ${BRAND}`,
    seoDescription: `Werde ${BRAND}-Autor: Erstelle interaktive Novellen und erhalte 20 % der damit erzielten Einnahmen. Melde dich bei uns.`,
    content: (
      <>
        <h2>Werde {BRAND}-Autor</h2>
        <p>
          Wir suchen aktiv talentierte Autorinnen und Autoren, die eigene interaktive
          Geschichten erschaffen und mit unserer Community teilen möchten.
        </p>
        <p>
          Autoren erhalten 20 % dessen, was ihre Novelle einspielt. Das ist deine Chance, aus
          dem Schreiben eine Einnahmequelle zu machen — bau Welten, die Tausende von Spielern
          begeistern.
        </p>
        <h2>Kontakt</h2>
        <p>
          E-Mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  french: {
    title: '🤝 Collaboration',
    description: `Deviens auteur ${BRAND} — crée des romans et gagne de l'argent.`,
    seoTitle: `Collaboration — ${BRAND}`,
    seoDescription: `Deviens auteur ${BRAND} : crée des romans interactifs et perçois 20 % des revenus qu'ils génèrent. Contacte-nous pour commencer.`,
    content: (
      <>
        <h2>Deviens auteur {BRAND}</h2>
        <p>
          Nous recherchons activement des auteurs talentueux qui souhaitent créer des histoires
          interactives originales et les partager avec notre communauté.
        </p>
        <p>
          Les auteurs perçoivent 20 % de ce que rapporte leur roman. C&apos;est ta chance de
          transformer l&apos;écriture en source de revenus — crée des mondes qui inspireront des
          milliers de joueurs.
        </p>
        <h2>Nous contacter</h2>
        <p>
          E-mail : <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  spanish: {
    title: '🤝 Colaboración',
    description: `Conviértete en autor de ${BRAND}: crea novelas y gana con ellas.`,
    seoTitle: `Colaboración — ${BRAND}`,
    seoDescription: `Conviértete en autor de ${BRAND}: crea novelas interactivas y recibe el 20 % de los ingresos que generen. Escríbenos para empezar.`,
    content: (
      <>
        <h2>Conviértete en autor de {BRAND}</h2>
        <p>
          Buscamos activamente autores con talento que quieran crear historias interactivas
          originales y compartirlas con nuestra comunidad.
        </p>
        <p>
          Los autores reciben el 20 % de lo que genere su novela. Es tu oportunidad de convertir
          la escritura en una fuente de ingresos: crea mundos que inspirarán a miles de
          jugadores.
        </p>
        <h2>Contacto</h2>
        <p>
          Correo electrónico: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  italian: {
    title: '🤝 Collabora con noi',
    description: `Diventa autore ${BRAND}: crea romanzi e guadagna con essi.`,
    seoTitle: `Collabora con noi — ${BRAND}`,
    seoDescription: `Diventa autore ${BRAND}: crea romanzi interattivi e ricevi il 20% dei ricavi che generano. Scrivici per iniziare.`,
    content: (
      <>
        <h2>Diventa autore {BRAND}</h2>
        <p>
          Siamo alla ricerca attiva di autori di talento che vogliano creare storie interattive
          originali e condividerle con la nostra community.
        </p>
        <p>
          Gli autori ricevono il 20% di quanto guadagna il loro romanzo. È la tua occasione per
          trasformare la scrittura in una fonte di reddito: crea mondi capaci di ispirare
          migliaia di giocatori.
        </p>
        <h2>Contatti</h2>
        <p>
          E-mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  polish: {
    title: '🤝 Współpraca',
    description: `Zostań autorem ${BRAND} — twórz powieści i zarabiaj na nich.`,
    seoTitle: `Współpraca — ${BRAND}`,
    seoDescription: `Zostań autorem ${BRAND}: twórz interaktywne powieści i otrzymuj 20% przychodów, które generują. Napisz do nas, aby zacząć.`,
    content: (
      <>
        <h2>Zostań autorem {BRAND}</h2>
        <p>
          Aktywnie poszukujemy utalentowanych autorów, którzy chcą tworzyć oryginalne
          interaktywne historie i dzielić się nimi z naszą społecznością.
        </p>
        <p>
          Autorzy otrzymują 20% tego, co zarobi ich powieść. To Twoja szansa, aby zamienić
          pisanie w źródło dochodu — twórz światy, które zainspirują tysiące graczy.
        </p>
        <h2>Kontakt</h2>
        <p>
          E-mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },

  portuguese: {
    title: '🤝 Trabalhe conosco',
    description: `Torne-se autor da ${BRAND} — crie novels e ganhe dinheiro com elas.`,
    seoTitle: `Trabalhe conosco — ${BRAND}`,
    seoDescription: `Torne-se autor da ${BRAND}: crie novels interativas e receba 20% da receita que elas gerarem. Entre em contato para começar a trabalhar com a gente.`,
    content: (
      <>
        <h2>Torne-se autor da {BRAND}</h2>
        <p>
          Estamos buscando ativamente autores talentosos que queiram criar histórias interativas
          únicas e compartilhá-las com a nossa comunidade.
        </p>
        <p>
          Os autores recebem 20% do que a sua novel faturar. Esta é a sua chance de transformar a
          escrita em fonte de renda — crie mundos que vão inspirar milhares de jogadores.
        </p>
        <h2>Fale com a gente</h2>
        <p>
          E-mail: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
        </p>
      </>
    ),
  },
};

export default post;
